/**
 * ViewportRenderer - Efficient viewport-based rendering for large documents
 * 
 * Provides high-performance rendering that only processes visible content,
 * enabling smooth scrolling and editing in 500+ page legal documents.
 */

interface ViewportBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

interface RenderChunk {
  chunkId: string;
  bounds: ViewportBounds;
  content: RenderableContent;
  isVisible: boolean;
  priority: 'high' | 'medium' | 'low';
  lastRendered: Date;
}

interface RenderableContent {
  lines: RenderableLine[];
  totalHeight: number;
  totalWidth: number;
  glyphCount: number;
}

interface RenderableLine {
  lineId: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  glyphs: RenderableGlyph[];
  visible: boolean;
}

interface RenderableGlyph {
  character: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontFamily: string;
  fontSize: number;
  color: string;
  visible: boolean;
}

interface RenderingMetrics {
  totalRenderTime: number;
  visibleChunks: number;
  renderedGlyphs: number;
  skippedGlyphs: number;
  frameRate: number;
  renderEfficiency: number; // Rendered pixels / Total pixels
  scrollPerformance: number;
}

export class ViewportRenderer {
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private viewport: ViewportBounds;
  private renderChunks: Map<string, RenderChunk> = new Map();
  
  // Rendering configuration
  private qualityMode: 'high' | 'medium' | 'performance' = 'high';
  private enableSubpixelRendering: boolean = true;
  private enableTextHinting: boolean = true;
  private maxRenderTime: number = 16; // 60fps target
  private renderBudget: number = 10;   // ms per frame for rendering
  
  // Performance tracking
  private metrics: RenderingMetrics;
  private renderQueue: RenderChunk[] = [];
  private isRendering: boolean = false;
  private lastFrameTime: number = 0;
  
  // Optimization settings
  private config: any;

  constructor(config: any) {
    this.config = config;
    
    this.viewport = {
      top: 0,
      bottom: window.innerHeight,
      left: 0,
      right: window.innerWidth,
      width: window.innerWidth,
      height: window.innerHeight
    };
    
    this.metrics = {
      totalRenderTime: 0,
      visibleChunks: 0,
      renderedGlyphs: 0,
      skippedGlyphs: 0,
      frameRate: 60,
      renderEfficiency: 1.0,
      scrollPerformance: 0
    };
    
    this.setupViewportTracking();
  }

  // ============================================================================
  // VIEWPORT RENDERING
  // ============================================================================

  /**
   * Initialize viewport rendering with canvas
   */
  initializeViewport(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.context = canvas.getContext('2d');
    
    if (!this.context) {
      throw new Error('Failed to get 2D rendering context');
    }
    
    // Configure context for legal document rendering
    this.configureRenderingContext();
    
    // Setup viewport update monitoring
    this.setupViewportTracking();
    
    console.log('[ViewportRenderer] Viewport rendering initialized');
  }

  /**
   * Render visible viewport content
   */
  async renderViewport(): Promise<{
    renderTime: number;
    chunksRendered: number;
    glyphsRendered: number;
  }> {
    if (!this.canvas || !this.context || this.isRendering) {
      return { renderTime: 0, chunksRendered: 0, glyphsRendered: 0 };
    }
    
    this.isRendering = true;
    const startTime = performance.now();
    
    try {
      // Clear canvas
      this.clearViewport();
      
      // Get visible chunks
      const visibleChunks = this.getVisibleChunks();
      
      // Render chunks in priority order
      let chunksRendered = 0;
      let glyphsRendered = 0;
      
      for (const chunk of visibleChunks) {
        const renderResult = await this.renderChunk(chunk);
        
        if (renderResult.rendered) {
          chunksRendered++;
          glyphsRendered += renderResult.glyphCount;
        }
        
        // Check render budget
        const elapsedTime = performance.now() - startTime;
        if (elapsedTime > this.renderBudget) {
          console.log(`[ViewportRenderer] Render budget exceeded: ${elapsedTime.toFixed(1)}ms`);
          break;
        }
      }
      
      const renderTime = performance.now() - startTime;
      
      // Update metrics
      this.updateRenderingMetrics(renderTime, chunksRendered, glyphsRendered);
      
      return { renderTime, chunksRendered, glyphsRendered };
      
    } finally {
      this.isRendering = false;
    }
  }

  /**
   * Render individual chunk
   */
  private async renderChunk(chunk: RenderChunk): Promise<{
    rendered: boolean;
    glyphCount: number;
  }> {
    if (!this.context || !chunk.content) {
      return { rendered: false, glyphCount: 0 };
    }
    
    let glyphCount = 0;
    
    // Render lines in chunk
    for (const line of chunk.content.lines) {
      if (this.isLineVisible(line)) {
        const lineGlyphs = await this.renderLine(line);
        glyphCount += lineGlyphs;
      }
    }
    
    chunk.lastRendered = new Date();
    
    return { rendered: true, glyphCount };
  }

  /**
   * Render individual line
   */
  private async renderLine(line: RenderableLine): Promise<number> {
    if (!this.context) return 0;
    
    let renderedGlyphs = 0;
    
    // Set line formatting
    this.applyLineFormatting(line);
    
    // Render glyphs
    for (const glyph of line.glyphs) {
      if (this.isGlyphVisible(glyph)) {
        await this.renderGlyph(glyph);
        renderedGlyphs++;
      }
    }
    
    return renderedGlyphs;
  }

  /**
   * Render individual glyph
   */
  private async renderGlyph(glyph: RenderableGlyph): Promise<void> {
    if (!this.context) return;
    
    // Apply glyph formatting
    this.context.fillStyle = glyph.color;
    this.context.font = `${glyph.fontSize}px ${glyph.fontFamily}`;
    
    // Render character
    if (this.qualityMode === 'high' && this.enableSubpixelRendering) {
      // High-quality subpixel rendering
      this.context.fillText(glyph.character, glyph.x, glyph.y);
    } else {
      // Fast integer rendering
      this.context.fillText(glyph.character, Math.round(glyph.x), Math.round(glyph.y));
    }
  }

  // ============================================================================
  // VIEWPORT MANAGEMENT
  // ============================================================================

  /**
   * Update viewport bounds
   */
  updateViewport(bounds: Partial<ViewportBounds>): void {
    this.viewport = { ...this.viewport, ...bounds };
    
    // Update visible chunks
    this.updateVisibleChunks();
    
    // Schedule re-render
    this.scheduleRender();
  }

  /**
   * Setup viewport tracking
   */
  private setupViewportTracking(): void {
    // Track scroll events
    let scrollTimeout: NodeJS.Timeout | null = null;
    
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        this.handleViewportChange();
      }, 8); // ~120fps scroll tracking
    };
    
    // Track resize events
    const handleResize = () => {
      this.updateViewport({
        width: window.innerWidth,
        height: window.innerHeight,
        right: window.innerWidth,
        bottom: window.innerHeight
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    
    console.log('[ViewportRenderer] Viewport tracking active');
  }

  private handleViewportChange(): void {
    const scrollTop = window.scrollY;
    const scrollLeft = window.scrollX;
    
    this.updateViewport({
      top: scrollTop,
      bottom: scrollTop + window.innerHeight,
      left: scrollLeft,
      right: scrollLeft + window.innerWidth
    });
    
    // Track scroll performance
    const scrollTime = performance.now() - this.lastFrameTime;
    this.metrics.scrollPerformance = (this.metrics.scrollPerformance * 0.9) + (scrollTime * 0.1);
    this.lastFrameTime = performance.now();
  }

  /**
   * Update visible chunks based on viewport
   */
  private updateVisibleChunks(): void {
    for (const [chunkId, chunk] of this.renderChunks) {
      const wasVisible = chunk.isVisible;
      chunk.isVisible = this.isChunkVisible(chunk);
      
      // Update priority based on visibility
      if (chunk.isVisible && !wasVisible) {
        chunk.priority = 'high';
        this.addToRenderQueue(chunk);
      } else if (!chunk.isVisible && wasVisible) {
        this.removeFromRenderQueue(chunk);
      }
    }
    
    this.metrics.visibleChunks = Array.from(this.renderChunks.values())
      .filter(chunk => chunk.isVisible).length;
  }

  /**
   * Check if chunk is visible in viewport
   */
  private isChunkVisible(chunk: RenderChunk): boolean {
    return !(chunk.bounds.bottom < this.viewport.top ||
             chunk.bounds.top > this.viewport.bottom ||
             chunk.bounds.right < this.viewport.left ||
             chunk.bounds.left > this.viewport.right);
  }

  /**
   * Check if line is visible in viewport
   */
  private isLineVisible(line: RenderableLine): boolean {
    return !(line.y + line.height < this.viewport.top ||
             line.y > this.viewport.bottom);
  }

  /**
   * Check if glyph is visible in viewport
   */
  private isGlyphVisible(glyph: RenderableGlyph): boolean {
    return !(glyph.x + glyph.width < this.viewport.left ||
             glyph.x > this.viewport.right ||
             glyph.y + glyph.height < this.viewport.top ||
             glyph.y > this.viewport.bottom);
  }

  // ============================================================================
  // RENDER QUEUE MANAGEMENT
  // ============================================================================

  /**
   * Add chunk to render queue
   */
  private addToRenderQueue(chunk: RenderChunk): void {
    if (!this.renderQueue.includes(chunk)) {
      this.renderQueue.push(chunk);
      
      // Sort by priority
      this.renderQueue.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });
    }
  }

  /**
   * Remove chunk from render queue
   */
  private removeFromRenderQueue(chunk: RenderChunk): void {
    const index = this.renderQueue.indexOf(chunk);
    if (index >= 0) {
      this.renderQueue.splice(index, 1);
    }
  }

  /**
   * Process render queue
   */
  private async processRenderQueue(): Promise<void> {
    if (this.isRendering || this.renderQueue.length === 0) return;
    
    const startTime = performance.now();
    
    while (this.renderQueue.length > 0 && 
           (performance.now() - startTime) < this.renderBudget) {
      
      const chunk = this.renderQueue.shift();
      if (chunk && chunk.isVisible) {
        await this.renderChunk(chunk);
      }
    }
  }

  /**
   * Schedule render using requestAnimationFrame
   */
  private scheduleRender(): void {
    requestAnimationFrame(() => {
      this.processRenderQueue();
    });
  }

  // ============================================================================
  // RENDERING OPTIMIZATION
  // ============================================================================

  /**
   * Configure rendering context for optimal performance
   */
  private configureRenderingContext(): void {
    if (!this.context) return;
    
    // Enable text rendering optimizations
    this.context.textBaseline = 'top';
    this.context.textAlign = 'left';
    
    // Configure quality based on mode
    switch (this.qualityMode) {
      case 'high':
        this.context.imageSmoothingEnabled = true;
        this.context.imageSmoothingQuality = 'high';
        break;
        
      case 'medium':
        this.context.imageSmoothingEnabled = true;
        this.context.imageSmoothingQuality = 'medium';
        break;
        
      case 'performance':
        this.context.imageSmoothingEnabled = false;
        break;
    }
    
    console.log(`[ViewportRenderer] Rendering context configured for ${this.qualityMode} quality`);
  }

  /**
   * Apply line formatting to context
   */
  private applyLineFormatting(line: RenderableLine): void {
    if (!this.context) return;
    
    // Extract formatting from first glyph
    const firstGlyph = line.glyphs[0];
    if (firstGlyph) {
      this.context.font = `${firstGlyph.fontSize}px ${firstGlyph.fontFamily}`;
      this.context.fillStyle = firstGlyph.color;
    }
  }

  /**
   * Clear viewport
   */
  private clearViewport(): void {
    if (!this.canvas || !this.context) return;
    
    // Clear only visible area for performance
    this.context.clearRect(
      this.viewport.left,
      this.viewport.top,
      this.viewport.width,
      this.viewport.height
    );
  }

  /**
   * Get visible chunks in viewport
   */
  private getVisibleChunks(): RenderChunk[] {
    return Array.from(this.renderChunks.values())
      .filter(chunk => chunk.isVisible)
      .sort((a, b) => {
        // Sort by priority then by position
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        
        if (priorityDiff !== 0) return priorityDiff;
        
        return a.bounds.top - b.bounds.top;
      });
  }

  // ============================================================================
  // PERFORMANCE OPTIMIZATION
  // ============================================================================

  /**
   * Set rendering quality mode
   */
  setQualityMode(mode: 'high' | 'medium' | 'performance'): void {
    this.qualityMode = mode;
    
    // Reconfigure rendering context
    this.configureRenderingContext();
    
    // Adjust render budget based on quality
    switch (mode) {
      case 'high':
        this.renderBudget = 16;   // Full frame budget
        this.enableSubpixelRendering = true;
        this.enableTextHinting = true;
        break;
        
      case 'medium':
        this.renderBudget = 12;   // Reduced budget
        this.enableSubpixelRendering = true;
        this.enableTextHinting = false;
        break;
        
      case 'performance':
        this.renderBudget = 8;    // Minimal budget
        this.enableSubpixelRendering = false;
        this.enableTextHinting = false;
        break;
    }
    
    console.log(`[ViewportRenderer] Quality mode set to ${mode} (${this.renderBudget}ms budget)`);
  }

  /**
   * Optimize rendering for current performance
   */
  async optimizeRendering(): Promise<{
    optimizationsApplied: string[];
    performanceImprovement: number;
  }> {
    const optimizationsApplied: string[] = [];
    const initialFrameRate = this.metrics.frameRate;
    
    // Reduce quality if performance is poor
    if (this.metrics.frameRate < 30 && this.qualityMode !== 'performance') {
      this.setQualityMode('performance');
      optimizationsApplied.push('reduced_quality');
    }
    
    // Reduce visible chunks if memory is high
    if (this.metrics.renderedGlyphs > 50000) {
      this.reduceVisibleChunks();
      optimizationsApplied.push('reduced_visible_chunks');
    }
    
    // Enable aggressive culling
    if (this.metrics.renderEfficiency < 0.5) {
      this.enableAggressiveCulling();
      optimizationsApplied.push('aggressive_culling');
    }
    
    // Wait for next frame to measure improvement
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    const performanceImprovement = this.metrics.frameRate - initialFrameRate;
    
    console.log(`[ViewportRenderer] Optimization complete: ${optimizationsApplied.length} optimizations, ${performanceImprovement.toFixed(1)}fps improvement`);
    
    return { optimizationsApplied, performanceImprovement };
  }

  private reduceVisibleChunks(): void {
    // Reduce the number of visible chunks for performance
    const visibleChunks = this.getVisibleChunks();
    const maxVisible = Math.max(1, Math.floor(visibleChunks.length * 0.7));
    
    // Hide lower priority chunks
    for (let i = maxVisible; i < visibleChunks.length; i++) {
      visibleChunks[i].isVisible = false;
      visibleChunks[i].priority = 'low';
    }
    
    console.log(`[ViewportRenderer] Reduced visible chunks to ${maxVisible}`);
  }

  private enableAggressiveCulling(): void {
    // Enable more aggressive off-screen culling
    const cullMargin = 100; // pixels
    
    for (const chunk of this.renderChunks.values()) {
      if (chunk.bounds.bottom < this.viewport.top - cullMargin ||
          chunk.bounds.top > this.viewport.bottom + cullMargin) {
        chunk.isVisible = false;
      }
    }
    
    console.log('[ViewportRenderer] Enabled aggressive culling');
  }

  // ============================================================================
  // CHUNK MANAGEMENT
  // ============================================================================

  /**
   * Add render chunk
   */
  addRenderChunk(
    chunkId: string,
    bounds: ViewportBounds,
    content: RenderableContent
  ): void {
    const chunk: RenderChunk = {
      chunkId,
      bounds,
      content,
      isVisible: this.isChunkInViewport(bounds),
      priority: this.isChunkInViewport(bounds) ? 'high' : 'low',
      lastRendered: new Date()
    };
    
    this.renderChunks.set(chunkId, chunk);
    
    if (chunk.isVisible) {
      this.addToRenderQueue(chunk);
    }
  }

  /**
   * Remove render chunk
   */
  removeRenderChunk(chunkId: string): void {
    const chunk = this.renderChunks.get(chunkId);
    if (chunk) {
      this.removeFromRenderQueue(chunk);
      this.renderChunks.delete(chunkId);
    }
  }

  /**
   * Update render chunk content
   */
  updateRenderChunk(chunkId: string, content: RenderableContent): void {
    const chunk = this.renderChunks.get(chunkId);
    if (chunk) {
      chunk.content = content;
      
      if (chunk.isVisible) {
        this.addToRenderQueue(chunk);
      }
    }
  }

  private isChunkInViewport(bounds: ViewportBounds): boolean {
    return !(bounds.bottom < this.viewport.top ||
             bounds.top > this.viewport.bottom ||
             bounds.right < this.viewport.left ||
             bounds.left > this.viewport.right);
  }

  // ============================================================================
  // PERFORMANCE MONITORING
  // ============================================================================

  /**
   * Update rendering metrics
   */
  private updateRenderingMetrics(
    renderTime: number,
    chunksRendered: number,
    glyphsRendered: number
  ): void {
    // Update metrics with exponential moving average
    const alpha = 0.1;
    
    this.metrics.totalRenderTime = (alpha * renderTime) + ((1 - alpha) * this.metrics.totalRenderTime);
    this.metrics.renderedGlyphs = glyphsRendered;
    this.metrics.visibleChunks = chunksRendered;
    
    // Calculate frame rate
    const targetFrameTime = 1000 / 60; // 16.67ms for 60fps
    this.metrics.frameRate = Math.min(60, 1000 / Math.max(renderTime, 1));
    
    // Calculate render efficiency
    const totalGlyphs = Array.from(this.renderChunks.values())
      .reduce((total, chunk) => total + (chunk.content?.glyphCount || 0), 0);
    
    this.metrics.renderEfficiency = totalGlyphs > 0 
      ? glyphsRendered / totalGlyphs 
      : 1.0;
    
    // Log performance warnings
    if (renderTime > this.maxRenderTime) {
      console.warn(`[ViewportRenderer] Slow render: ${renderTime.toFixed(1)}ms (target: ${this.maxRenderTime}ms)`);
    }
    
    if (this.metrics.frameRate < 30) {
      console.warn(`[ViewportRenderer] Low frame rate: ${this.metrics.frameRate.toFixed(1)}fps`);
    }
  }

  /**
   * Get rendering statistics
   */
  getRenderingStatistics(): {
    averageRenderTime: number;
    currentFrameRate: number;
    renderEfficiency: number;
    visibleChunks: number;
    totalChunks: number;
    memoryUsage: number;
  } {
    return {
      averageRenderTime: this.metrics.totalRenderTime,
      currentFrameRate: this.metrics.frameRate,
      renderEfficiency: this.metrics.renderEfficiency,
      visibleChunks: this.metrics.visibleChunks,
      totalChunks: this.renderChunks.size,
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  private estimateMemoryUsage(): number {
    // Estimate memory usage of render chunks
    let totalMemory = 0;
    
    for (const chunk of this.renderChunks.values()) {
      if (chunk.content) {
        // Estimate memory for lines and glyphs
        const lineMemory = chunk.content.lines.length * 100; // ~100 bytes per line
        const glyphMemory = chunk.content.glyphCount * 50;   // ~50 bytes per glyph
        totalMemory += lineMemory + glyphMemory;
      }
    }
    
    return totalMemory / (1024 * 1024); // Convert to MB
  }

  // ============================================================================
  // VIRTUAL SCROLLING
  // ============================================================================

  /**
   * Enable virtual scrolling for large documents
   */
  enableVirtualScrolling(): void {
    if (!this.config.enableVirtualization) return;
    
    console.log('[ViewportRenderer] Virtual scrolling enabled');
    
    // Create virtual scroll container
    this.createVirtualScrollContainer();
    
    // Setup virtual scroll events
    this.setupVirtualScrollEvents();
  }

  private createVirtualScrollContainer(): void {
    // Create virtual container that represents full document height
    const totalHeight = this.calculateTotalDocumentHeight();
    
    // Update document container height
    const event = new CustomEvent('edgedocs-virtual-scroll-height', {
      detail: { totalHeight }
    });
    window.dispatchEvent(event);
  }

  private calculateTotalDocumentHeight(): number {
    let totalHeight = 0;
    
    for (const chunk of this.renderChunks.values()) {
      totalHeight += chunk.content?.totalHeight || 0;
    }
    
    return totalHeight;
  }

  private setupVirtualScrollEvents(): void {
    // Handle virtual scroll position changes
    window.addEventListener('edgedocs-virtual-scroll', (event: any) => {
      const { scrollTop, scrollLeft } = event.detail;
      
      this.updateViewport({
        top: scrollTop,
        bottom: scrollTop + this.viewport.height,
        left: scrollLeft,
        right: scrollLeft + this.viewport.width
      });
    });
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get viewport bounds
   */
  getViewport(): ViewportBounds {
    return { ...this.viewport };
  }

  /**
   * Get rendering metrics
   */
  getRenderingMetrics(): RenderingMetrics {
    return { ...this.metrics };
  }

  /**
   * Force immediate render
   */
  async forceRender(): Promise<void> {
    console.log('[ViewportRenderer] Force render requested');
    await this.renderViewport();
  }

  /**
   * Update configuration
   */
  updateConfiguration(newConfig: any): void {
    this.config = { ...this.config, ...newConfig };
    
    // Apply configuration changes
    if (newConfig.maxRenderTime) {
      this.maxRenderTime = newConfig.maxRenderTime;
    }
    
    if (newConfig.renderBudget) {
      this.renderBudget = newConfig.renderBudget;
    }
    
    console.log('[ViewportRenderer] Configuration updated');
  }

  /**
   * Get visible content bounds
   */
  getVisibleContentBounds(): {
    startPosition: number;
    endPosition: number;
    visibleLines: number;
  } {
    const visibleChunks = this.getVisibleChunks();
    
    if (visibleChunks.length === 0) {
      return { startPosition: 0, endPosition: 0, visibleLines: 0 };
    }
    
    const startPosition = Math.min(...visibleChunks.map(c => c.bounds.top));
    const endPosition = Math.max(...visibleChunks.map(c => c.bounds.bottom));
    const visibleLines = visibleChunks.reduce((total, chunk) => 
      total + (chunk.content?.lines.filter(line => this.isLineVisible(line)).length || 0), 0
    );
    
    return { startPosition, endPosition, visibleLines };
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy viewport renderer
   */
  destroy(): void {
    // Clear render queue
    this.renderQueue = [];
    
    // Clear chunks
    this.renderChunks.clear();
    
    // Remove event listeners
    window.removeEventListener('scroll', this.handleViewportChange);
    window.removeEventListener('resize', this.handleViewportChange);
    
    console.log('[ViewportRenderer] Destroyed');
  }
}



